<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  BuildingOffice2Icon,
  PaperClipIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

interface TicketAttachment {
  id?: string | number
  url?: string
  filename?: string
  name?: string
}

interface TicketMessage {
  id: string | number
  author: string
  authorDetail?: string
  date: number
  comment: string
  supportComment?: boolean
  attachments?: TicketAttachment[]
}

const route = useRoute()
const suiteStore = useSuiteStore()
const permissionStore = useBoostCorePermissionStore()

const ticket = ref<any>(null)
const loading = ref(false)
const notFound = ref(false)

const ticketUuid = computed(() => String(route.params.ticketUuid ?? ''))

// Les admins tickets arrivant ici via un lien partagé peuvent basculer sur la vue admin
const isTicketAdmin = computed(
  () => permissionStore.include('TICKETDEV_ADMIN') || permissionStore.include('SUPER_ADMIN'),
)

async function load() {
  if (!ticketUuid.value) return
  loading.value = true
  notFound.value = false
  ticket.value = null
  try {
    // Endpoint non-admin : ne renvoie que les commentaires publics
    const response = await suiteStore.suiteFetch(`/tickets/${ticketUuid.value}/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!response || typeof response === 'boolean' || !response.ok) {
      notFound.value = true
      return
    }
    ticket.value = await response.json()
  } catch {
    notFound.value = true
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(ticketUuid, load)

const requesterContact = computed(() => ticket.value?.ticketRequester?.contact ?? null)
const requesterUser = computed(() => ticket.value?.ticketRequester?.user ?? null)
const company = computed(() => requesterContact.value?.company ?? null)

const requesterName = computed(() => {
  const contact = requesterContact.value
  if (contact) return [contact.firstname, contact.lastname].filter(Boolean).join(' ') || contact.email || 'Contact'
  const user = requesterUser.value
  if (user) return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Utilisateur'
  return 'Demandeur inconnu'
})

const requesterEmail = computed(() => requesterContact.value?.email ?? requesterUser.value?.email ?? null)

function toMs(date: number): number {
  return date < 10000000000 ? date * 1000 : date
}

// La description du ticket est affichée comme premier message du fil
const messages = computed<TicketMessage[]>(() => {
  if (!ticket.value) return []
  const comments = [...(ticket.value.comments || [])].sort(
    (a, b) => toMs(a.date ?? 0) - toMs(b.date ?? 0),
  )
  return [
    {
      id: 'ticket-description',
      author: requesterName.value,
      date: new Date(ticket.value.dateCreated).getTime(),
      comment: ticket.value.description,
      supportComment: false,
      attachments: ticket.value.attachments || [],
    },
    ...comments,
  ]
})

function formatDate(timestamp?: number | string) {
  if (!timestamp) return '—'
  const ms = typeof timestamp === 'string' ? new Date(timestamp).getTime() : toMs(timestamp)
  return new Date(ms).toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusTranslations: Record<string, string> = {
  NEW: 'NOUVEAU',
  OPEN: 'OUVERT',
  WAITING: 'EN ATTENTE',
  PAUSED: 'EN PAUSE',
  RESOLVED: 'RÉSOLU',
  CLOSED: 'FERMÉ',
}

const statusColors: Record<string, string> = {
  NEW: 'badge-info',
  OPEN: 'badge-success',
  WAITING: 'badge-warning',
  PAUSED: 'badge-ghost',
  RESOLVED: 'badge-accent',
  CLOSED: 'badge-error',
}
</script>

<template>
  <div class="p-6">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <Bs2Title
        :title="ticket ? `Ticket #${ticket.internalReference}` : 'Ticket'"
        :subtitle="ticket?.title ?? ''"
      />
      <router-link
        v-if="isTicketAdmin && ticket"
        :to="`/admin/tickets/${ticket.uuid}`"
        class="btn btn-sm btn-ghost gap-1"
      >
        <WrenchScrewdriverIcon class="w-4 h-4" />
        Ouvrir dans l'admin
      </router-link>
    </div>

    <div v-if="ticket" class="mt-4 flex flex-col lg:flex-row gap-4 items-start">
      <!-- Fil de discussion (lecture seule) -->
      <div class="flex-1 w-full min-w-0 card bg-base-100 shadow">
        <div class="card-body p-4">
          <div v-if="messages.length" class="flex flex-col gap-4">
            <div
              v-for="message in messages"
              :key="message.id"
              class="flex flex-col"
              :class="message.supportComment ? 'items-start' : 'items-end'"
            >
              <div
                class="max-w-[80%] rounded-lg shadow-sm border"
                :class="message.supportComment
                  ? 'bg-primary border-primary text-primary-content'
                  : 'bg-base-100 border-base-300 text-base-content'"
              >
                <div class="p-4">
                  <div
                    class="flex items-start justify-between gap-3 mb-2 pb-2 border-b"
                    :class="message.supportComment ? 'border-primary-content/20' : 'border-base-300'"
                  >
                    <div class="flex flex-col gap-0.5">
                      <span class="font-semibold">{{ message.author }}</span>
                      <span
                        v-if="message.authorDetail"
                        class="text-xs italic"
                        :class="message.supportComment ? 'text-primary-content/80' : 'text-base-content/60'"
                      >
                        {{ message.authorDetail }}
                      </span>
                    </div>
                    <span
                      class="text-xs shrink-0 pt-0.5"
                      :class="message.supportComment ? 'text-primary-content/80' : 'text-base-content/60'"
                    >
                      {{ formatDate(message.date) }}
                    </span>
                  </div>
                  <div class="text-[0.9375rem] leading-relaxed whitespace-pre-wrap">
                    {{ message.comment }}
                  </div>

                  <div v-if="message.attachments && message.attachments.length" class="mt-3 flex flex-wrap gap-2">
                    <a
                      v-for="file in message.attachments"
                      :key="file.id"
                      :href="file.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-2 px-2 py-1 rounded border text-xs transition-colors"
                      :class="message.supportComment
                        ? 'bg-primary-content/10 border-primary-content/30 text-primary-content hover:bg-primary-content/20'
                        : 'bg-base-200 border-base-300 text-base-content/70 hover:bg-base-300'"
                    >
                      <PaperClipIcon class="w-3 h-3" />
                      <span class="truncate max-w-[150px]">{{ file.filename || file.name }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="border border-dashed border-base-300 rounded-lg p-8 text-center text-base-content/60">
            Aucun message pour le moment.
          </div>
        </div>
      </div>

      <!-- Métadonnées -->
      <div class="w-full lg:w-80 shrink-0 flex flex-col gap-4">
        <div class="card bg-base-100 shadow">
          <div class="card-body p-5 space-y-4">
            <h3 class="text-sm font-bold text-base-content/60 uppercase tracking-wider">Détails du ticket</h3>

            <div class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-base-content/60 mb-1">Référence</label>
                <div class="text-sm font-mono font-semibold">#{{ ticket.internalReference }}</div>
              </div>

              <div>
                <label class="block text-xs font-medium text-base-content/60 mb-1">Statut</label>
                <span :class="['badge', statusColors[ticket.ticketStatus] ?? 'badge-ghost']">
                  {{ statusTranslations[ticket.ticketStatus] ?? ticket.ticketStatus }}
                </span>
              </div>

              <div>
                <label class="block text-xs font-medium text-base-content/60 mb-1">Créé le</label>
                <div class="text-sm">{{ formatDate(ticket.dateCreated) }}</div>
              </div>

              <div>
                <label class="block text-xs font-medium text-base-content/60 mb-1">Mis à jour le</label>
                <div class="text-sm">{{ formatDate(ticket.dateUpdated) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="card bg-base-100 shadow">
          <div class="card-body p-5 space-y-3">
            <h3 class="text-sm font-bold text-base-content/60 uppercase tracking-wider">Demandeur</h3>

            <div class="flex items-center gap-2 text-sm">
              <UserIcon class="w-4 h-4 text-base-content/50 flex-shrink-0" />
              <div class="min-w-0">
                <div class="font-medium truncate">{{ requesterName }}</div>
                <div v-if="requesterEmail" class="text-xs text-base-content/60 truncate">{{ requesterEmail }}</div>
              </div>
            </div>

            <div v-if="company" class="flex items-center gap-2 text-sm">
              <BuildingOffice2Icon class="w-4 h-4 text-base-content/50 flex-shrink-0" />
              <div class="min-w-0">
                <div class="font-medium truncate">{{ company.name }}</div>
                <div v-if="company.siret" class="text-xs text-base-content/60">SIRET {{ company.siret }}</div>
              </div>
            </div>

            <div class="flex flex-col gap-2 pt-2 border-t border-base-300">
              <router-link
                v-if="requesterContact"
                :to="`/dashboard/contacts/${requesterContact.uuid}`"
                class="btn btn-sm btn-outline w-full"
              >
                Fiche contact
              </router-link>
              <router-link
                v-if="company?.referenceExterne"
                :to="`/partner/${company.referenceExterne}`"
                class="btn btn-sm btn-outline w-full"
              >
                Fiche client
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="notFound" class="mt-4 card bg-base-100 shadow">
      <div class="card-body items-center text-center py-12 text-base-content/60">
        <p class="text-lg font-medium">Ticket introuvable</p>
        <p class="text-sm">Le lien est peut-être invalide ou le ticket a été supprimé.</p>
      </div>
    </div>

    <div v-else class="flex flex-col items-center justify-center p-12 gap-4 text-base-content/60">
      <span class="loading loading-spinner loading-lg"></span>
      <p>Chargement du ticket…</p>
    </div>
  </div>
</template>
